import { AlertCircle, CheckCircle2, CircleCheck, FlaskConical } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui';
import type { WorklistMetrics } from '../api/verificationApi';
import { pluralize } from '../utils/format';
import styles from './AttentionCards.module.css';

interface AttentionCardsProps {
  metrics: WorklistMetrics;
  onReview: () => void;
  onFix: () => void;
}

export function AttentionCards({ metrics, onReview, onFix }: AttentionCardsProps) {
  const { awaitingReview, failed, awaitingProduction } = metrics;
  const nothing = awaitingReview === 0 && failed === 0 && awaitingProduction === 0;

  if (nothing) {
    return (
      <div className={styles.card}>
        <div className={clsx(styles.icon, styles.iconSuccess)}>
          <CheckCircle2 size={17} aria-hidden />
        </div>
        <div className={styles.text}>
          <div className={styles.title}>Nothing needs your attention right now</div>
          <div className={styles.sub}>
            New appointments are checked automatically as they come in from a sync or an import.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stack}>
      {awaitingReview > 0 && (
        <div className={styles.card}>
          <div className={styles.icon}>
            <CircleCheck size={17} aria-hidden />
          </div>
          <div className={styles.text}>
            <div className={styles.title}>
              {pluralize(awaitingReview, 'verification is', 'verifications are')} ready for review
            </div>
            <div className={styles.sub}>
              Benefits gathered · waiting on your sign-off to write back to Open Dental
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={onReview}>
            Review {awaitingReview} →
          </Button>
        </div>
      )}

      {failed > 0 && (
        <div className={styles.card}>
          <div className={clsx(styles.icon, styles.iconError)}>
            <AlertCircle size={17} aria-hidden />
          </div>
          <div className={styles.text}>
            <div className={styles.title}>{pluralize(failed, 'verification')} failed</div>
            <div className={styles.sub}>
              Usually a member ID or date-of-birth mismatch, or a carrier without a payer match ·
              open each one to correct it
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onFix}>
            Fix →
          </Button>
        </div>
      )}

      {awaitingProduction > 0 && (
        <div className={styles.card}>
          <div className={clsx(styles.icon, styles.iconPurple)}>
            <FlaskConical size={17} aria-hidden />
          </div>
          <div className={styles.text}>
            <div className={styles.title}>
              {pluralize(awaitingProduction, 'verification is', 'verifications are')} awaiting
              production
            </div>
            <div className={styles.sub}>
              Stedi is in test mode — these carriers have no mock; they'll run once Stedi is
              switched to production
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
